import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Users, Building2, FolderOpen, Briefcase, Calendar, MessageSquare, LifeBuoy, TrendingUp, Shield, Handshake, Heart, AlertTriangle, Database, Loader2, ArrowDownToLine, Globe, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [enhancedStats, setEnhancedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordResetStats, setPasswordResetStats] = useState({ pending: 0, total: 0 });
  const [actionItemsCount, setActionItemsCount] = useState(0);
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  
  // Remote migration state
  const [migrationSource, setMigrationSource] = useState('https://pro-radar-r-1769298134.emergent.host');
  const [migrationExclude, setMigrationExclude] = useState('Alldus');
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationExecuting, setMigrationExecuting] = useState(false);
  
  // Direct DB migration state
  const [dbMigrationMongoUrl, setDbMigrationMongoUrl] = useState('mongodb+srv://consult-connect-50:d4km90slqs2c73f1qqog@customer-apps.tmubzu.mongodb.net/?appName=email-standardize&maxPoolSize=5&retryWrites=true&timeoutMS=10000&w=majority');
  const [dbMigrationDbName, setDbMigrationDbName] = useState('snow_exchange');
  const [dbMigrationExclude, setDbMigrationExclude] = useState('Alldus');
  const [dbMigrationPreview, setDbMigrationPreview] = useState(null);
  const [dbMigrationLoading, setDbMigrationLoading] = useState(false);
  const [dbMigrationExecuting, setDbMigrationExecuting] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchPasswordResetStats();
    fetchEnhancedStats();
    fetchActionItems();
  }, []);

  const fetchStats = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnhancedStats = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/enhanced-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnhancedStats(response.data);
    } catch (error) {
      console.error('Error fetching enhanced stats:', error);
    }
  };

  const fetchActionItems = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/action-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActionItemsCount(response.data.total_items || 0);
    } catch (error) {
      console.error('Error fetching action items:', error);
    }
  };

  const runDiagnostic = async () => {
    const token = localStorage.getItem('token');
    setDiagLoading(true);
    try {
      const response = await axios.get(`${API}/admin/diagnostic`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnostic(response.data);
    } catch (error) {
      toast.error('Failed to run diagnostic');
    } finally {
      setDiagLoading(false);
    }
  };

  const runMerge = async () => {
    if (!window.confirm('This will merge all users and data from other databases into the current one. Duplicates will be skipped. Continue?')) return;
    const token = localStorage.getItem('token');
    setMerging(true);
    try {
      const response = await axios.post(`${API}/admin/diagnostic/merge-databases`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Merge complete: ${response.data.total_documents_merged} documents merged`);
      setDiagnostic(null);
      runDiagnostic();
      fetchStats();
    } catch (error) {
      toast.error('Merge failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMerging(false);
    }
  };

  const fetchPasswordResetStats = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/password-resets?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const resets = response.data.resets || [];
      const now = new Date();
      const pending = resets.filter(r => !r.used && new Date(r.expires_at) > now).length;
      setPasswordResetStats({ pending, total: resets.length });
    } catch (error) {
      console.error('Error fetching password reset stats:', error);
    }
  };

  const runMigrationPreview = async () => {
    const token = localStorage.getItem('token');
    setMigrationLoading(true);
    setMigrationPreview(null);
    try {
      const response = await axios.post(`${API}/admin/migrate-from-remote`, {
        source_url: migrationSource,
        dry_run: true,
        exclude_company_names: migrationExclude.split(',').map(s => s.trim()).filter(Boolean)
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMigrationPreview(response.data);
      toast.success('Preview generated successfully');
    } catch (error) {
      toast.error('Preview failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMigrationLoading(false);
    }
  };

  const executeMigration = async () => {
    if (!window.confirm('CONFIRM: This will import all previewed data into the current production database. Excluded companies will be skipped. This action cannot be easily undone. Continue?')) return;
    const token = localStorage.getItem('token');
    setMigrationExecuting(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-from-remote`, {
        source_url: migrationSource,
        dry_run: false,
        exclude_company_names: migrationExclude.split(',').map(s => s.trim()).filter(Boolean)
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMigrationPreview(response.data);
      const c = response.data.companies?.imported || 0;
      const r = response.data.resources?.imported || 0;
      const p = response.data.projects?.imported || 0;
      toast.success(`Migration complete! Imported: ${c} companies, ${r} resources, ${p} projects`);
      fetchStats();
    } catch (error) {
      toast.error('Migration failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMigrationExecuting(false);
    }
  };

  const runDbMigrationPreview = async () => {
    if (!dbMigrationMongoUrl) { toast.error('Please enter the source MongoDB URL'); return; }
    const token = localStorage.getItem('token');
    setDbMigrationLoading(true);
    setDbMigrationPreview(null);
    try {
      const response = await axios.post(`${API}/admin/migrate-direct-db`, {
        source_mongo_url: dbMigrationMongoUrl,
        source_db_name: dbMigrationDbName,
        dry_run: true,
        exclude_company_names: dbMigrationExclude.split(',').map(s => s.trim()).filter(Boolean)
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 });
      setDbMigrationPreview(response.data);
      if (response.data.errors?.length > 0) {
        toast.error('Preview completed with errors');
      } else {
        toast.success('Preview generated - check details below');
      }
    } catch (error) {
      toast.error('DB Preview failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDbMigrationLoading(false);
    }
  };

  const executeDbMigration = async () => {
    if (!window.confirm('MIGRATION COMPLETE (base-à-base): Ceci va importer TOUS les utilisateurs (avec mots de passe), entreprises, ressources et projets. Les doublons seront ignorés. Alldus sera exclu. Continuer ?')) return;
    const token = localStorage.getItem('token');
    setDbMigrationExecuting(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-direct-db`, {
        source_mongo_url: dbMigrationMongoUrl,
        source_db_name: dbMigrationDbName,
        dry_run: false,
        exclude_company_names: dbMigrationExclude.split(',').map(s => s.trim()).filter(Boolean)
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
      setDbMigrationPreview(response.data);
      const u = response.data.users?.imported || 0;
      const c = response.data.companies?.imported || 0;
      const r = response.data.resources?.imported || 0;
      const p = response.data.projects?.imported || 0;
      toast.success(`Migration complete! ${u} users, ${c} companies, ${r} resources, ${p} projects imported`);
      fetchStats();
    } catch (error) {
      toast.error('DB Migration failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDbMigrationExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#62D84E' }}></div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: '#3b82f6' },
    { label: 'Companies', value: stats?.total_companies || 0, icon: Building2, color: '#8b5cf6' },
    { label: 'Resources', value: stats?.total_resources || 0, icon: FolderOpen, color: '#62D84E' },
    { label: 'Active Resources', value: stats?.active_resources || 0, icon: TrendingUp, color: '#22c55e' },
    { label: 'Projects', value: stats?.total_projects || 0, icon: Briefcase, color: '#f59e0b' },
    { label: 'Active Projects', value: stats?.active_projects || 0, icon: TrendingUp, color: '#f97316' },
    { label: 'Reservations', value: stats?.total_reservations || 0, icon: Calendar, color: '#06b6d4' },
    { label: 'Pending Reservations', value: stats?.pending_reservations || 0, icon: Calendar, color: '#eab308' },
    // New: Collaboration stats
    { label: 'Collaborations', value: enhancedStats?.collaborations?.total || 0, icon: Handshake, color: '#10b981' },
    { label: 'Ongoing Collaborations', value: enhancedStats?.collaborations?.ongoing || 0, icon: Handshake, color: '#059669' },
    { label: 'Completed Collaborations', value: enhancedStats?.collaborations?.completed || 0, icon: Handshake, color: '#047857' },
    // New: Favorites stats
    { label: 'Total Favorites', value: enhancedStats?.favorites?.total || 0, icon: Heart, color: '#ec4899' },
    { label: 'Resource Favorites', value: enhancedStats?.favorites?.resources || 0, icon: Heart, color: '#f472b6' },
    { label: 'Company Favorites', value: enhancedStats?.favorites?.companies || 0, icon: Heart, color: '#db2777' },
    // Action Items
    { label: 'Action Items', value: actionItemsCount, icon: AlertTriangle, color: '#ef4444', link: '/admin/action-items' },
    // Password Resets
    { label: 'Password Resets', value: passwordResetStats.total, icon: Shield, color: '#6366f1', link: '/admin/password-resets' },
    { label: 'Pending Resets', value: passwordResetStats.pending, icon: Shield, color: '#8b5cf6', link: '/admin/password-resets' },
    // Support
    { label: 'Support Tickets', value: stats?.total_tickets || 0, icon: LifeBuoy, color: '#ef4444' },
    { label: 'Open Tickets', value: stats?.open_tickets || 0, icon: LifeBuoy, color: '#dc2626' },
  ];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Admin Dashboard</h1>
        <p className="text-gray-600 mt-2">Platform overview and statistics</p>
      </div>

      {/* Alert Banner for Action Items */}
      {actionItemsCount > 0 && (
        <Link to="/admin/action-items">
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between cursor-pointer hover:bg-red-100 transition-colors">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <div>
                <p className="font-semibold text-red-800">Action Required</p>
                <p className="text-sm text-red-600">{actionItemsCount} items need your attention</p>
              </div>
            </div>
            <span className="text-red-500 text-sm">View All →</span>
          </div>
        </Link>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const CardWrapper = stat.link ? Link : 'div';
          const cardProps = stat.link ? { to: stat.link } : {};
          
          return (
            <CardWrapper key={index} {...cardProps}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                    <span>{stat.label}</span>
                    <Icon className="w-5 h-5" style={{ color: stat.color }} />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold" style={{ color: stat.color }}>
                    {(stat.value || 0).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            </CardWrapper>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4" style={{ color: '#1B3240' }}>Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Link to="/admin/action-items">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow h-full">
              <CardContent className="p-6 flex items-center space-x-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="font-semibold">Action Items</p>
                  <p className="text-sm text-gray-600">{actionItemsCount} items pending</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/users">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow h-full">
              <CardContent className="p-6 flex items-center space-x-4">
                <Users className="w-8 h-8" style={{ color: '#62D84E' }} />
                <div>
                  <p className="font-semibold">Manage Users</p>
                  <p className="text-sm text-gray-600">View and edit user accounts</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/collaborations">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow h-full">
              <CardContent className="p-6 flex items-center space-x-4">
                <Handshake className="w-8 h-8 text-green-500" />
                <div>
                  <p className="font-semibold">Collaborations</p>
                  <p className="text-sm text-gray-600">{enhancedStats?.collaborations?.ongoing || 0} ongoing</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/tickets">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow h-full">
              <CardContent className="p-6 flex items-center space-x-4">
                <LifeBuoy className="w-8 h-8 text-red-500" />
                <div>
                  <p className="font-semibold">Support Tickets</p>
                  <p className="text-sm text-gray-600">{stats?.open_tickets || 0} open tickets</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>

      {/* Database Diagnostic */}
      <div className="mt-8" data-testid="db-diagnostic-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold" style={{ color: '#1B3240' }}>Database Diagnostic</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={runDiagnostic}
            disabled={diagLoading}
            data-testid="run-diagnostic-btn"
          >
            {diagLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
            Run Diagnostic
          </Button>
        </div>

        {/* Remote App Migration Tool */}
        <Card className="mb-6 border-indigo-200 bg-indigo-50/30" data-testid="remote-migration-section">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-indigo-900">Remote App Data Migration</h3>
            </div>
            <p className="text-xs text-indigo-700 mb-4">
              Migrate companies, resources, and projects from another Emergent live app into the current database. 
              Use this to recover data from the Email-Standardize deployment.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Source App URL</label>
                <input
                  type="text"
                  value={migrationSource}
                  onChange={(e) => setMigrationSource(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                  placeholder="https://pro-radar-r-....emergent.host"
                  data-testid="migration-source-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Exclude Companies (comma-separated)</label>
                <input
                  type="text"
                  value={migrationExclude}
                  onChange={(e) => setMigrationExclude(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                  placeholder="Alldus, OtherCompany"
                  data-testid="migration-exclude-input"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={runMigrationPreview}
                disabled={migrationLoading || !migrationSource}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                data-testid="migration-preview-btn"
              >
                {migrationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
                Preview Migration (Dry Run)
              </Button>
              {migrationPreview && migrationPreview.dry_run && (migrationPreview.companies?.to_import > 0 || migrationPreview.resources?.to_import > 0 || migrationPreview.projects?.to_import > 0) && (
                <Button
                  size="sm"
                  onClick={executeMigration}
                  disabled={migrationExecuting}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="migration-execute-btn"
                >
                  {migrationExecuting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowDownToLine className="w-4 h-4 mr-2" />}
                  Execute Migration
                </Button>
              )}
            </div>

            {/* Migration Preview/Results */}
            {migrationPreview && (
              <div className="mt-4 space-y-3" data-testid="migration-results">
                {migrationPreview.errors?.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800 flex items-center gap-1"><XCircle className="w-4 h-4" /> Errors</p>
                    {migrationPreview.errors.map((e, i) => <p key={i} className="text-xs text-red-600 mt-1">{e}</p>)}
                  </div>
                )}

                {/* Summary */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Companies', data: migrationPreview.companies, color: 'blue' },
                    { label: 'Resources', data: migrationPreview.resources, color: 'green' },
                    { label: 'Projects', data: migrationPreview.projects, color: 'amber' }
                  ].map(({ label, data, color }) => (
                    <div key={label} className={`p-3 bg-${color}-50 border border-${color}-200 rounded-lg`}>
                      <p className={`text-sm font-semibold text-${color}-900`}>{label}</p>
                      <div className="text-xs mt-1 space-y-0.5">
                        <p>Found: <strong>{data?.found || 0}</strong></p>
                        <p className="text-green-700">To import: <strong>{data?.to_import || 0}</strong></p>
                        {!migrationPreview.dry_run && <p className="text-green-800">Imported: <strong>{data?.imported || 0}</strong></p>}
                        <p className="text-gray-500">Already exists: {data?.skipped_existing || 0}</p>
                        <p className="text-orange-600">Excluded: {data?.skipped_excluded || 0}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Excluded companies */}
                {migrationPreview.excluded_companies?.length > 0 && (
                  <div className="p-2 bg-orange-50 border border-orange-200 rounded text-xs">
                    <span className="font-medium text-orange-800">Excluded: </span>
                    <span className="text-orange-700">{migrationPreview.excluded_companies.join(', ')}</span>
                  </div>
                )}

                {/* Detailed company list */}
                {migrationPreview.companies?.details?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-gray-500 border-b sticky top-0 bg-white"><th className="pb-1 pr-3">Company</th><th className="pb-1 pr-3">Type</th><th className="pb-1">Status</th></tr></thead>
                      <tbody>
                        {migrationPreview.companies.details.map((c, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-1 pr-3 font-medium">{c.name}</td>
                            <td className="py-1 pr-3 capitalize">{c.type || '—'}</td>
                            <td className="py-1">
                              {c.status === 'to_import' && <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Ready to import</span>}
                              {c.status === 'already_exists' && <span className="text-gray-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Already exists</span>}
                              {c.status === 'imported' && <span className="text-green-700 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Imported</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Success message */}
                {!migrationPreview.dry_run && migrationPreview.errors?.length === 0 && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <p className="text-sm font-medium text-green-800">
                      Migration completed successfully! Imported {migrationPreview.companies?.imported || 0} companies, {migrationPreview.resources?.imported || 0} resources, {migrationPreview.projects?.imported || 0} projects.
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Direct Database Migration (Full - with passwords) */}
        <Card className="mb-6 border-emerald-200 bg-emerald-50/30" data-testid="direct-db-migration-section">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-emerald-900">Direct Database Migration (Full)</h3>
              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Recommended</span>
            </div>
            <p className="text-xs text-emerald-700 mb-4">
              Connect directly to the source MongoDB database. Copies <strong>everything</strong> including user accounts with password hashes.
              Users will be able to log in with their existing credentials — fully transparent migration.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="md:col-span-1">
                <label className="text-xs font-medium text-gray-700 block mb-1">Source MONGO_URL</label>
                <input
                  type="text"
                  value={dbMigrationMongoUrl}
                  onChange={(e) => setDbMigrationMongoUrl(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 font-mono"
                  placeholder="mongodb+srv://user:pass@cluster..."
                  data-testid="db-migration-mongo-url"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Database Name</label>
                <input
                  type="text"
                  value={dbMigrationDbName}
                  onChange={(e) => setDbMigrationDbName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
                  placeholder="snow_exchange"
                  data-testid="db-migration-db-name"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Exclude Companies</label>
                <input
                  type="text"
                  value={dbMigrationExclude}
                  onChange={(e) => setDbMigrationExclude(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
                  placeholder="Alldus, OtherCompany"
                  data-testid="db-migration-exclude"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={runDbMigrationPreview}
                disabled={dbMigrationLoading || !dbMigrationMongoUrl}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                data-testid="db-migration-preview-btn"
              >
                {dbMigrationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
                Preview (Dry Run)
              </Button>
              {dbMigrationPreview && dbMigrationPreview.dry_run && !dbMigrationPreview.errors?.length && (
                <Button
                  size="sm"
                  onClick={executeDbMigration}
                  disabled={dbMigrationExecuting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="db-migration-execute-btn"
                >
                  {dbMigrationExecuting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowDownToLine className="w-4 h-4 mr-2" />}
                  Execute Full Migration
                </Button>
              )}
            </div>

            {/* DB Migration Results */}
            {dbMigrationPreview && (
              <div className="mt-4 space-y-3" data-testid="db-migration-results">
                {dbMigrationPreview.errors?.length > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800 flex items-center gap-1"><XCircle className="w-4 h-4" /> Errors</p>
                    {dbMigrationPreview.errors.map((e, i) => <p key={i} className="text-xs text-red-600 mt-1">{e}</p>)}
                  </div>
                )}

                {/* Summary - 4 columns for users, companies, resources, projects */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Users', data: dbMigrationPreview.users, color: 'indigo' },
                    { label: 'Companies', data: dbMigrationPreview.companies, color: 'blue' },
                    { label: 'Resources', data: dbMigrationPreview.resources, color: 'green' },
                    { label: 'Projects', data: dbMigrationPreview.projects, color: 'amber' }
                  ].map(({ label, data, color }) => (
                    <div key={label} className={`p-3 rounded-lg border`} style={{ backgroundColor: `var(--${color}-50, #f0fdf4)`, borderColor: `var(--${color}-200, #bbf7d0)` }}>
                      <p className="text-sm font-semibold" style={{ color: '#1B3240' }}>{label}</p>
                      <div className="text-xs mt-1 space-y-0.5">
                        <p>Found: <strong>{data?.found || 0}</strong></p>
                        <p className="text-green-700">To import: <strong>{data?.to_import || 0}</strong></p>
                        {!dbMigrationPreview.dry_run && <p className="text-green-800 font-semibold">Imported: <strong>{data?.imported || 0}</strong></p>}
                        <p className="text-gray-500">Existing: {data?.skipped_existing || 0}</p>
                        <p className="text-orange-600">Excluded: {data?.skipped_excluded || 0}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Excluded companies */}
                {dbMigrationPreview.detected_db_name && (
                  <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                    <span className="font-medium text-blue-800">Detected database: </span>
                    <span className="text-blue-700 font-mono">{dbMigrationPreview.detected_db_name}</span>
                    {dbMigrationPreview.available_databases?.length > 0 && (
                      <span className="text-blue-500 ml-2">(available: {dbMigrationPreview.available_databases.join(', ')})</span>
                    )}
                  </div>
                )}
                {dbMigrationPreview.excluded_companies?.length > 0 && (
                  <div className="p-2 bg-orange-50 border border-orange-200 rounded text-xs">
                    <span className="font-medium text-orange-800">Excluded: </span>
                    <span className="text-orange-700">{dbMigrationPreview.excluded_companies.join(', ')}</span>
                  </div>
                )}

                {/* User details table */}
                {dbMigrationPreview.users?.details?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-1">User Accounts</p>
                    <div className="max-h-48 overflow-y-auto border rounded">
                      <table className="w-full text-xs">
                        <thead><tr className="text-left text-gray-500 border-b sticky top-0 bg-gray-50"><th className="p-1.5">Email</th><th className="p-1.5">Role</th><th className="p-1.5">Company</th><th className="p-1.5">Password</th><th className="p-1.5">Status</th></tr></thead>
                        <tbody>
                          {dbMigrationPreview.users.details.map((u, i) => (
                            <tr key={i} className="border-b last:border-0">
                              <td className="p-1.5 font-medium">{u.email}</td>
                              <td className="p-1.5 capitalize">{u.role?.replace('_', ' ') || '—'}</td>
                              <td className="p-1.5">{u.company || '—'}</td>
                              <td className="p-1.5">{u.has_password ? <span className="text-green-600">Yes</span> : <span className="text-red-500">No</span>}</td>
                              <td className="p-1.5">
                                {u.status === 'to_import' && <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Ready</span>}
                                {u.status === 'already_exists' && <span className="text-gray-500">Exists</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Other collections */}
                {Object.keys(dbMigrationPreview.other_collections || {}).length > 0 && (
                  <div className="p-3 bg-gray-50 border rounded-lg">
                    <p className="text-xs font-medium text-gray-700 mb-2">Other Collections</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      {Object.entries(dbMigrationPreview.other_collections).map(([name, info]) => (
                        <div key={name} className="bg-white p-2 rounded border">
                          <p className="font-medium">{name}</p>
                          {info.error ? (
                            <p className="text-red-500">{info.error}</p>
                          ) : (
                            <p className="text-gray-600">{info.to_import} to import / {info.skipped} skipped</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Success message */}
                {!dbMigrationPreview.dry_run && !dbMigrationPreview.errors?.length && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Migration complete!
                      </p>
                      <p className="text-xs text-green-700 mt-0.5">
                        {dbMigrationPreview.users?.imported || 0} users (with passwords), {dbMigrationPreview.companies?.imported || 0} companies, {dbMigrationPreview.resources?.imported || 0} resources, {dbMigrationPreview.projects?.imported || 0} projects imported successfully.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {diagnostic && (
          <div className="space-y-4">
            {/* Summary cards */}
            <Card className="border-blue-200">
              <CardContent className="p-5">
                <p className="text-sm text-gray-500 mb-3">
                  Current DB: <strong className="text-gray-900">{diagnostic.current_database}</strong>
                  {' '}&bull;{' '}All databases: <strong>{diagnostic.all_databases?.join(', ')}</strong>
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Active Users', value: diagnostic.summary?.active_users, color: 'text-green-700 bg-green-50' },
                    { label: 'Soft-Deleted Users', value: diagnostic.summary?.soft_deleted_users, color: diagnostic.summary?.soft_deleted_users > 0 ? 'text-red-700 bg-red-50' : 'text-gray-600 bg-gray-50' },
                    { label: 'Active Companies', value: diagnostic.summary?.active_companies, color: 'text-blue-700 bg-blue-50' },
                    { label: 'Other DBs with Data', value: diagnostic.summary?.other_dbs_with_data, color: diagnostic.summary?.other_dbs_with_data > 0 ? 'text-amber-700 bg-amber-50' : 'text-gray-600 bg-gray-50' }
                  ].map((s, i) => (
                    <div key={i} className={`p-3 rounded-lg text-center ${s.color}`}>
                      <p className="text-2xl font-bold">{s.value}</p>
                      <p className="text-xs">{s.label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Other databases with users — MERGE */}
            {diagnostic.other_databases && Object.keys(diagnostic.other_databases).length > 0 && (
              <Card className="border-amber-300 bg-amber-50">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-amber-900">Data found in other databases</p>
                    <Button
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      size="sm"
                      onClick={runMerge}
                      disabled={merging}
                      data-testid="merge-databases-btn"
                    >
                      {merging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
                      Merge All Into Current DB
                    </Button>
                  </div>
                  {Object.entries(diagnostic.other_databases).map(([dbName, info]) => (
                    <div key={dbName} className="mb-4 p-3 bg-white rounded-lg border border-amber-200">
                      <p className="font-medium text-amber-800 text-sm mb-1">
                        {dbName} — {info.users_count} users, {info.companies_count} companies, {info.resources_count} resources, {info.projects_count} projects
                      </p>
                      {info.users?.length > 0 && (
                        <table className="w-full text-xs mt-2">
                          <thead><tr className="text-left text-gray-500 border-b"><th className="pb-1 pr-3">Name</th><th className="pb-1 pr-3">Email</th><th className="pb-1 pr-3">Role</th><th className="pb-1">Company</th></tr></thead>
                          <tbody>
                            {info.users.map((u, i) => (
                              <tr key={i} className="border-b last:border-0">
                                <td className="py-1 pr-3 font-medium">{u.first_name} {u.last_name}</td>
                                <td className="py-1 pr-3">{u.email}</td>
                                <td className="py-1 pr-3 capitalize">{u.role?.replace('_', ' ')}</td>
                                <td className="py-1">{u.company_name}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Soft-deleted users — RESTORE */}
            {diagnostic.deleted_users?.length > 0 && (
              <Card className="border-red-300 bg-red-50">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-red-900">Soft-deleted users ({diagnostic.deleted_users.length})</p>
                    <Button
                      className="bg-red-600 hover:bg-red-700 text-white"
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm('Restore ALL deleted users, companies, resources and projects?')) return;
                        const token = localStorage.getItem('token');
                        try {
                          const res = await axios.post(`${API}/admin/diagnostic/restore-deleted`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success(`Restored: ${res.data.restored.users} users, ${res.data.restored.companies} companies, ${res.data.restored.resources} resources, ${res.data.restored.projects} projects`);
                          runDiagnostic();
                          fetchStats();
                        } catch (e) { toast.error('Restore failed'); }
                      }}
                      data-testid="restore-all-btn"
                    >
                      Restore All Deleted
                    </Button>
                  </div>
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-gray-500 border-b"><th className="pb-1 pr-3">Name</th><th className="pb-1 pr-3">Email</th><th className="pb-1 pr-3">Company</th><th className="pb-1 pr-3">Role</th><th className="pb-1 pr-3">Deleted</th><th className="pb-1">Action</th></tr></thead>
                    <tbody>
                      {diagnostic.deleted_users.map((u, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1.5 pr-3 font-medium">{u.first_name} {u.last_name}</td>
                          <td className="py-1.5 pr-3">{u.email}</td>
                          <td className="py-1.5 pr-3">{u.company_name}</td>
                          <td className="py-1.5 pr-3 capitalize">{u.role?.replace('_', ' ')}</td>
                          <td className="py-1.5 pr-3 text-red-600">{u.deleted_at ? new Date(u.deleted_at).toLocaleDateString() : '—'}</td>
                          <td className="py-1.5">
                            <button
                              className="text-blue-600 hover:underline text-xs"
                              onClick={async () => {
                                const token = localStorage.getItem('token');
                                try {
                                  await axios.post(`${API}/admin/diagnostic/restore-user/${u.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success(`Restored ${u.email}`);
                                  runDiagnostic(); fetchStats();
                                } catch (e) { toast.error('Restore failed'); }
                              }}
                            >Restore</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Deleted companies */}
            {diagnostic.deleted_companies?.length > 0 && (
              <Card className="border-orange-200">
                <CardContent className="p-4">
                  <p className="font-medium text-orange-800 text-sm mb-2">Soft-deleted companies ({diagnostic.deleted_companies.length})</p>
                  <div className="text-xs space-y-1">
                    {diagnostic.deleted_companies.map((c, i) => (
                      <p key={i}><strong>{c.name}</strong> — deleted {c.deleted_at ? new Date(c.deleted_at).toLocaleDateString() : '—'}</p>
                    ))}
                  </div>
                  <p className="text-xs text-orange-600 mt-2">These will be restored with "Restore All Deleted" above.</p>
                </CardContent>
              </Card>
            )}

            {/* Active users list */}
            <Card>
              <CardContent className="p-5">
                <p className="font-medium text-gray-800 text-sm mb-2">Active Users ({diagnostic.active_users?.length})</p>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-gray-500 border-b sticky top-0 bg-white"><th className="pb-1 pr-3">Name</th><th className="pb-1 pr-3">Email</th><th className="pb-1 pr-3">Company</th><th className="pb-1 pr-3">Role</th><th className="pb-1">Tier</th></tr></thead>
                    <tbody>
                      {diagnostic.active_users?.map((u, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1 pr-3 font-medium">{u.first_name} {u.last_name}</td>
                          <td className="py-1 pr-3">{u.email}</td>
                          <td className="py-1 pr-3">{u.company_name}</td>
                          <td className="py-1 pr-3 capitalize">{u.role?.replace('_', ' ')}</td>
                          <td className="py-1 capitalize">{u.subscription_tier}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* All good message */}
            {(!diagnostic.other_databases || Object.keys(diagnostic.other_databases).length === 0) && (!diagnostic.deleted_users || diagnostic.deleted_users.length === 0) && (
              <p className="text-sm text-green-600 text-center py-3">All data is in the current database. No deleted users, no missing data.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
